/** Walk-in guest at the desk (CI not found among ministers or registrations) */

import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import * as eventGuestsRepo from '@/data/eventGuestsRepository';
import { registerParticipant } from '@/Services/eventParticipants.service';
import { GuestRegistrationForm, type GuestFormData } from '../../components/GuestRegistrationForm';
import { validateGuestForm } from '../../utils/guestValidation';

interface DeskGuestDialogProps {
  eventId: string;
  initialDni: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRegistered: () => void;
}

export function DeskGuestDialog({ eventId, initialDni, open, onOpenChange, onRegistered }: DeskGuestDialogProps) {
  const { toast } = useToast();
  const [data, setData] = useState<GuestFormData>({ full_name: '', dni: initialDni });
  const [errors, setErrors] = useState<Partial<Record<keyof GuestFormData, string>>>({});
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const validation = validateGuestForm(data);
    if (!validation.valid) {
      setErrors(validation.errors);
      return;
    }
    setBusy(true);
    try {
      const guest = await eventGuestsRepo.findOrCreateEventGuest({
        full_name: data.full_name,
        dni: data.dni,
        email: data.email,
        phone: data.phone,
        age: data.age,
        church_affiliation: data.church_affiliation,
        representing_minister_id: data.representing_minister_id,
        is_delegate: false, // "invitado" at the desk is never a delegate
      });
      await registerParticipant({
        event_id: eventId,
        guest_id: guest.id,
        calculate_price: true,
        registration_data: { kind: 'GUEST', full_name: data.full_name, dni: data.dni, cell_phone: data.phone },
      });
      toast({ title: 'Invitado inscrito' });
      onOpenChange(false);
      onRegistered();
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'No se pudo inscribir', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Registrar invitado</DialogTitle>
          <DialogDescription>Persona que no es ministro registrado. Después podrá cobrar e imprimir su credencial.</DialogDescription>
        </DialogHeader>
        <GuestRegistrationForm eventId={eventId} value={data} onChange={setData} errors={errors} />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={busy || data.full_name.trim().length < 3}>
            {busy && <Loader2 className="mr-2 size-4 animate-spin" />}
            Inscribir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
